import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Grid,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Autocomplete,
  createFilterOptions,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CustomizedTextField from '../project/CustomizedTextField';
import NumericFormatInput from '../general/NumericFormatInput';
import DynamicDatePicker from '../common/DynamicDatePicker';
import { format, capitalize, filterDigitsHyphens } from '../../common/utils/common.util';
import useLookups from '../../hooks/useLookups';
import { LOOKUP_KEYS } from '../../common/constants/lookups';
import { City, State } from '../lookups/lookups.type';
import { getCityString, getStateLabel, isValidCityState } from '../lookups/lookups.util';
import { validateCityState } from '../lookups/lookups.api';
import useCityStateValidation from '../../hooks/useCityStateValidation';
import { toast } from 'react-toastify';
import { Button } from 'components/index';
import { CreateInvoiceDTO, ProjectData, BaseInvoiceDTO } from './invoice.type';

/**
 * Given the latest invoice number (e.g. "C12345-12"), returns the next one ("C12345-13").
 * Increments the numeric suffix after the last hyphen. If no numeric suffix exists, appends "-2".
 */
function getNextInvoiceNumber(latestInvoiceNumber: string): string {
  const lastDashIdx = latestInvoiceNumber.lastIndexOf('-');
  if (lastDashIdx !== -1) {
    const suffix = latestInvoiceNumber.slice(lastDashIdx + 1);
    const suffixNum = parseInt(suffix, 10);
    if (!isNaN(suffixNum)) {
      return `${latestInvoiceNumber.slice(0, lastDashIdx)}-${suffixNum + 1}`;
    }
  }
  return `${latestInvoiceNumber}-2`;
}

/**
 * Looks at all past (non-current) invoices and returns autofill defaults for a new invoice:
 * - invoiceNumber: "[ProjectNumber]-1" if no past invoices, otherwise increments the latest invoice number
 * - invoiceAmount: monitoringFee if provided, otherwise the latest past invoice's amount (0 if none)
 */
function computeAutoFillDefaults(
  allInvoices: BaseInvoiceDTO[],
  projectNumber: string,
  monitoringFee?: number,
): { invoiceNumber: string; invoiceAmount: number } {
  const pastInvoices = allInvoices.filter(inv => !(inv as any).currentInvoice);

  if (pastInvoices.length === 0) {
    return {
      invoiceNumber: projectNumber ? `${projectNumber}-1` : '',
      invoiceAmount: monitoringFee || 0,
    };
  }

  const sorted = [...pastInvoices].sort((a, b) => {
    const dateA = new Date(a.invoice.invoiceDate || (a as any).createdOn || '').getTime() || 0;
    const dateB = new Date(b.invoice.invoiceDate || (b as any).createdOn || '').getTime() || 0;
    return dateB - dateA;
  });

  const latest = sorted[0];
  const latestNumber = latest.invoice.number || '';
  const nextNumber = latestNumber ? getNextInvoiceNumber(latestNumber) : projectNumber ? `${projectNumber}-1` : '';

  return {
    invoiceNumber: nextNumber,
    invoiceAmount: monitoringFee || latest.invoice.invoiceAmount || 0,
  };
}

export interface CreateInvoiceProps {
  projectData?: ProjectData;
  isExportMode?: boolean;
  onSaveInvoice?: (invoice: CreateInvoiceDTO) => void | Promise<void>;
  onExportInvoice?: (invoice: CreateInvoiceDTO) => void;
  onCancel?: () => void;
  service?: string;
  isMonitoringReport?: boolean;
  currentInvoice?: BaseInvoiceDTO;
  mode?: 'regular' | 'current' | 'past';
  invoices?: BaseInvoiceDTO[];
  saveRef?: React.MutableRefObject<(() => Promise<boolean>) | null>;
}

const CreateInvoice: React.FC<CreateInvoiceProps> = ({
  projectData,
  isExportMode = false,
  onSaveInvoice,
  onExportInvoice,
  onCancel,
  service = '',
  isMonitoringReport = false,
  currentInvoice,
  mode = 'regular',
  invoices,
  saveRef,
}) => {
  const [newInvoice, setNewInvoice] = useState<Partial<CreateInvoiceDTO>>({});
  const [validationErrors, setValidationErrors] = useState<{ [key: string]: boolean }>({});
  const [hasChanges, setHasChanges] = useState(false);
  const [showComment, setShowComment] = useState(() => !!currentInvoice?.invoice?.comment);

  // Loading states
  const [isSaving, setIsSaving] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // Address state management
  const [recipientPostalCode, setRecipientPostalCode] = useState('');
  const [projectPostalCode, setProjectPostalCode] = useState('');
  const [recipientSelectedState, setRecipientSelectedState] = useState('');
  const [projectSelectedState, setProjectSelectedState] = useState('');
  const [recipientCity, setRecipientCity] = useState('');
  const [projectCityState, setProjectCityState] = useState('');
  // Manual State pick blocks city-derived auto-overwrite until a new city is chosen.
  const [recipientStateManuallySet, setRecipientStateManuallySet] = useState(false);
  const [projectStateManuallySet, setProjectStateManuallySet] = useState(false);

  const stateOptions = useLookups(LOOKUP_KEYS.States) as State[];
  const cityOptions = useLookups(LOOKUP_KEYS.Cities) as City[];

  const { status: recipientCityStateStatus, checkOnBlur: checkRecipientCityStateOnBlur } = useCityStateValidation(
    cityOptions,
    newInvoice.recepientAddress?.city,
    recipientSelectedState,
  );
  const { status: projectCityStateStatus, checkOnBlur: checkProjectCityStateOnBlur } = useCityStateValidation(
    cityOptions,
    newInvoice.projectAddress?.city,
    projectSelectedState,
  );
  const isRecipientCityStateInvalid = recipientCityStateStatus === 'invalid';
  const isProjectCityStateInvalid = projectCityStateStatus === 'invalid';

  // Initialize new invoice with project data or current invoice for limited reports
  useEffect(() => {
    if (isMonitoringReport && currentInvoice) {
      // Editing an existing current invoice — populate from its saved data
      const invoiceNumber = currentInvoice.invoice.number || '';

      const initialInvoice: Partial<CreateInvoiceDTO> = {
        recepient: currentInvoice.recepient || '',
        recepientAddress: currentInvoice.recepientAddress || {},
        projectName: currentInvoice.projectName || '',
        projectAddress: currentInvoice.projectAddress || {},
        service: currentInvoice.service || service,
        invoice: {
          number: invoiceNumber,
          invoiceDate: currentInvoice.invoice.invoiceDate || '',
          invoiceAmount: currentInvoice.invoice.invoiceAmount || 0,
          paidAmount: currentInvoice.invoice.paidAmount || 0,
          comment: currentInvoice.invoice.comment || '',
        },
      };

      setNewInvoice(
        prev =>
          ({
            ...initialInvoice,
            invoice: {
              ...initialInvoice.invoice,
              // Preserve locally-typed comment if backend hasn't returned it yet
              comment: currentInvoice.invoice.comment || prev?.invoice?.comment || '',
            },
          }) as Partial<CreateInvoiceDTO>,
      );

      // Once open, never close the comment box on re-render
      setShowComment(prev => prev || !!currentInvoice.invoice.comment);

      // Set address states
      if (currentInvoice.recepientAddress?.postalCode) {
        setRecipientPostalCode(currentInvoice.recepientAddress.postalCode);
      }
      if (currentInvoice.projectAddress?.postalCode) {
        setProjectPostalCode(currentInvoice.projectAddress.postalCode);
      }
      if (currentInvoice.recepientAddress?.state) {
        setRecipientSelectedState(capitalize(currentInvoice.recepientAddress.state) || '');
      }
      if (currentInvoice.projectAddress?.state) {
        setProjectSelectedState(capitalize(currentInvoice.projectAddress.state) || '');
      }
      // Reset manual-override tracking for the new record
      setRecipientStateManuallySet(false);
      setProjectStateManuallySet(false);
    } else if (projectData) {
      // New invoice — populate from project data
      const today = new Date().toISOString();
      const projectAddress = projectData.projectAddress;
      const clientAddress = projectData.clientAddress;

      // For monitoring reports (current or past mode), autofill invoice number and amount
      // from the latest past invoice. Falls back to "[ProjectNumber]-1" when no past invoices exist.
      let defaultInvoiceNumber = '';
      let defaultInvoiceAmount = 0;

      if (isMonitoringReport && (mode === 'current' || mode === 'past')) {
        const autoFill = computeAutoFillDefaults(projectData.invoices || [], projectData.projectNumber || '', projectData.monitoringFee);
        defaultInvoiceNumber = autoFill.invoiceNumber;
        defaultInvoiceAmount = autoFill.invoiceAmount;
      }

      const initialInvoice: Partial<CreateInvoiceDTO> = {
        recepient: projectData.clientName || '',
        recepientAddress: {
          street: clientAddress?.street || '',
          city: clientAddress?.city || '',
          state: clientAddress?.state || '',
          postalCode: clientAddress?.postalCode || '',
        },
        projectName: `${projectData.projectNumber || ''} ${projectData.projectTitle || ''}`.trim(),
        projectAddress: {
          street: projectAddress?.street || '',
          city: projectAddress?.city || '',
          state: projectAddress?.state || '',
          postalCode: projectAddress?.postalCode || '',
        },
        service: service,
        invoice: {
          number: defaultInvoiceNumber,
          invoiceDate: today,
          invoiceAmount: defaultInvoiceAmount,
          paidAmount: 0,
        },
      };

      setNewInvoice(initialInvoice);

      // Set address states
      if (clientAddress?.postalCode) {
        setRecipientPostalCode(clientAddress.postalCode);
      }
      if (projectAddress?.postalCode) {
        setProjectPostalCode(projectAddress.postalCode);
      }
      if (clientAddress?.state) {
        setRecipientSelectedState(capitalize(clientAddress.state) || '');
      }
      if (projectAddress?.state) {
        setProjectSelectedState(capitalize(projectAddress.state) || '');
      }
      // Reset manual-override tracking for the new record
      setRecipientStateManuallySet(false);
      setProjectStateManuallySet(false);
    }

    // Reset changes flag when initializing
    setHasChanges(false);
  }, [projectData, service, isMonitoringReport, currentInvoice]);

  // Check for changes when newInvoice changes
  useEffect(() => {
    if (isMonitoringReport && mode === 'current' && currentInvoice) {
      const hasInvoiceChanges =
        newInvoice.recepient !== currentInvoice.recepient ||
        newInvoice.projectName !== currentInvoice.projectName ||
        newInvoice.service !== currentInvoice.service ||
        newInvoice.invoice?.comment !== (currentInvoice.invoice.comment || '') ||
        newInvoice.invoice?.number !== currentInvoice.invoice.number ||
        newInvoice.invoice?.invoiceDate !== currentInvoice.invoice.invoiceDate ||
        newInvoice.invoice?.invoiceAmount !== currentInvoice.invoice.invoiceAmount ||
        newInvoice.invoice?.paidAmount !== currentInvoice.invoice.paidAmount ||
        JSON.stringify(newInvoice.recepientAddress) !== JSON.stringify(currentInvoice.recepientAddress) ||
        JSON.stringify(newInvoice.projectAddress) !== JSON.stringify(currentInvoice.projectAddress);

      setHasChanges(hasInvoiceChanges);
    } else {
      // For new invoices or past invoices, always allow saving
      setHasChanges(true);
    }
  }, [newInvoice, currentInvoice, isMonitoringReport, mode]);

  // Handle city changes for recipient
  useEffect(() => {
    if (recipientCity && !recipientStateManuallySet) {
      const stateCode = recipientCity.split(', ')[1];
      const matchingState = stateOptions.find(option => option.stateCode === stateCode);
      const stateName = capitalize(matchingState?.name || '') || '';
      setRecipientSelectedState(stateName);
      updateNewInvoiceField('recepientAddress.state', stateName);
    }
  }, [recipientCity, stateOptions, recipientStateManuallySet]);

  // Handle city changes for project
  useEffect(() => {
    if (projectCityState && !projectStateManuallySet) {
      const stateCode = projectCityState.split(', ')[1];
      const matchingState = stateOptions.find(option => option.stateCode === stateCode);
      const stateName = capitalize(matchingState?.name || '') || '';
      setProjectSelectedState(stateName);
      updateNewInvoiceField('projectAddress.state', stateName);
    }
  }, [projectCityState, stateOptions, projectStateManuallySet]);

  // Resolves state from city for records with a city but no real state, once city options load.
  useEffect(() => {
    if (cityOptions.length === 0) return;

    const recCity = newInvoice.recepientAddress?.city;
    const recState = newInvoice.recepientAddress?.state;
    if (recCity && !recState && !recipientCity) {
      const match = cityOptions.find(c => c.name.toLowerCase() === recCity.toLowerCase());
      if (match?.state?.stateCode) {
        setRecipientCity(`${match.name}, ${match.state.stateCode}`);
      }
    }

    const projCity = newInvoice.projectAddress?.city;
    const projState = newInvoice.projectAddress?.state;
    if (projCity && !projState && !projectCityState) {
      const match = cityOptions.find(c => c.name.toLowerCase() === projCity.toLowerCase());
      if (match?.state?.stateCode) {
        setProjectCityState(`${match.name}, ${match.state.stateCode}`);
      }
    }
  }, [
    cityOptions,
    newInvoice.recepientAddress?.city,
    newInvoice.recepientAddress?.state,
    newInvoice.projectAddress?.city,
    newInvoice.projectAddress?.state,
    recipientCity,
    projectCityState,
  ]);

  const updateNewInvoiceField = (field: string, value: any) => {
    setNewInvoice(prev => {
      const updated = { ...prev };
      const fieldPath = field.split('.');

      if (fieldPath.length === 1) {
        (updated as any)[fieldPath[0]] = value;
      } else if (fieldPath.length === 2) {
        const [parent, child] = fieldPath;
        if (!updated[parent as keyof typeof updated]) {
          (updated as any)[parent] = {};
        }
        ((updated as any)[parent] as any)[child] = value;
      }

      return updated;
    });
  };

  const handleSaveInvoice = useCallback(async (): Promise<boolean> => {
    if (isSaving) return false;

    // Reset validation errors
    setValidationErrors({});

    // Check required fields
    const errors: { [key: string]: boolean } = {};
    if (!newInvoice.recepient?.trim()) errors.recepient = true;
    if (!newInvoice.projectName?.trim()) errors.projectName = true;
    if (!newInvoice.invoice?.number?.trim()) errors.invoiceNumber = true;
    if (!newInvoice.invoice?.invoiceDate) errors.invoiceDate = true;
    if (!newInvoice.invoice?.invoiceAmount || newInvoice.invoice.invoiceAmount <= 0) errors.invoiceAmount = true;
    if (newInvoice.invoice?.paidAmount === undefined) errors.paidAmount = true;

    if (isRecipientCityStateInvalid) errors.recipientCityStateInvalid = true;
    if (isProjectCityStateInvalid) errors.projectCityStateInvalid = true;

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      toast.error(
        errors.recipientCityStateInvalid || errors.projectCityStateInvalid
          ? 'Please fix the invalid city/state combination'
          : 'Please fill in all required fields',
      );
      return false;
    }

    if (recipientCityStateStatus === 'checking' || projectCityStateStatus === 'checking') {
      toast.error('Still verifying city/state — please wait a moment and try again');
      return false;
    }

    const pendingChecks: { key: 'recipientCityStateInvalid' | 'projectCityStateInvalid'; city: string; state: string }[] = [];
    if (
      recipientCityStateStatus === 'unknown' &&
      newInvoice.recepientAddress?.city &&
      recipientSelectedState &&
      !isValidCityState(cityOptions, newInvoice.recepientAddress.city, recipientSelectedState)
    ) {
      pendingChecks.push({ key: 'recipientCityStateInvalid', city: newInvoice.recepientAddress.city, state: recipientSelectedState });
    }
    if (
      projectCityStateStatus === 'unknown' &&
      newInvoice.projectAddress?.city &&
      projectSelectedState &&
      !isValidCityState(cityOptions, newInvoice.projectAddress.city, projectSelectedState)
    ) {
      pendingChecks.push({ key: 'projectCityStateInvalid', city: newInvoice.projectAddress.city, state: projectSelectedState });
    }

    if (pendingChecks.length > 0) {
      setIsSaving(true);
      let hasInvalidPair = false;
      try {
        const results = await Promise.all(pendingChecks.map(check => validateCityState(check.city, check.state)));
        results.forEach((result, index) => {
          if (!result?.isValid) {
            errors[pendingChecks[index].key] = true;
            hasInvalidPair = true;
          }
        });
      } catch {
        // Network/geocode failure — fall through and let the save proceed rather than
        // blocking on an unrelated outage; local-lookup validation already passed above.
      } finally {
        setIsSaving(false);
      }

      if (hasInvalidPair) {
        setValidationErrors(errors);
        toast.error('Please fix the invalid city/state combination');
        return false;
      }
    }

    // Trim invoice number
    const trimmedInvoiceNumber = newInvoice.invoice?.number?.trim() || '';
    if (!trimmedInvoiceNumber) {
      toast.error('Invoice number is required');
      return false;
    }

    // Check for duplicate invoice numbers
    const allInvoices = [...(invoices || []), ...(projectData?.invoices || [])];

    // For limited reports with current invoice, exclude the current invoice from duplicate check
    const invoicesToCheck =
      isMonitoringReport && mode === 'current' && currentInvoice
        ? allInvoices.filter(invoice => invoice.invoice.number !== currentInvoice.invoice.number)
        : allInvoices;

    const isDuplicate = invoicesToCheck.some(
      (invoice: BaseInvoiceDTO) => invoice.invoice.number.trim().toLowerCase() === trimmedInvoiceNumber.toLowerCase(),
    );

    if (isDuplicate) {
      setValidationErrors({ ...errors, invoiceNumber: true });
      toast.error('Invoice number already exists');
      return false;
    }

    if (!newInvoice.invoice?.invoiceAmount || newInvoice.invoice.invoiceAmount <= 0) {
      toast.error('Invoice amount must be greater than 0');
      return false;
    }

    // Add currentInvoice flag for limited reports based on mode
    const updatedInvoice = {
      ...newInvoice,
      invoice: {
        ...newInvoice.invoice,
        number: trimmedInvoiceNumber,
      },
      currentInvoice: isMonitoringReport ? mode === 'current' : undefined,
    };

    // Add the invoice
    if (!onSaveInvoice) return false;

    setIsSaving(true);
    try {
      await onSaveInvoice(updatedInvoice as CreateInvoiceDTO);
      // Immediately disable the button after a successful save in current-invoice mode
      // to close the race window between isSaving reset and currentInvoice prop update
      if (isMonitoringReport && mode === 'current') {
        setHasChanges(false);
      }
    } catch {
      return false;
    } finally {
      setIsSaving(false);
    }

    if (!isMonitoringReport || mode === 'past') {
      setNewInvoice({});
    }

    return true;
  }, [
    isSaving,
    newInvoice,
    invoices,
    projectData,
    isMonitoringReport,
    mode,
    currentInvoice,
    onSaveInvoice,
    isRecipientCityStateInvalid,
    isProjectCityStateInvalid,
    recipientCityStateStatus,
    projectCityStateStatus,
    recipientSelectedState,
    projectSelectedState,
    cityOptions,
  ]);

  // Expose save function to parent (used by navigation guard so Next auto-saves)
  useEffect(() => {
    if (!saveRef) return;
    if (!isMonitoringReport || mode !== 'current') {
      saveRef.current = async () => true;
      return;
    }
    // No changes to save and invoice already exists → let navigation proceed
    if (currentInvoice && !hasChanges) {
      saveRef.current = async () => true;
      return;
    }
    saveRef.current = handleSaveInvoice;
    return () => {
      saveRef.current = null;
    };
  }, [saveRef, isMonitoringReport, mode, currentInvoice, hasChanges, handleSaveInvoice]);

  const handleExportInvoice = () => {
    // Prevent multiple clicks by checking if already exporting
    if (isExporting) return;

    // Reset validation errors
    setValidationErrors({});

    // Check required fields
    const errors: { [key: string]: boolean } = {};
    if (!newInvoice.recepient?.trim()) errors.recepient = true;
    if (!newInvoice.projectName?.trim()) errors.projectName = true;
    if (!newInvoice.invoice?.number?.trim()) errors.invoiceNumber = true;
    if (!newInvoice.invoice?.invoiceDate) errors.invoiceDate = true;
    if (!newInvoice.invoice?.invoiceAmount || newInvoice.invoice.invoiceAmount <= 0) errors.invoiceAmount = true;
    if (newInvoice.invoice?.paidAmount === undefined) errors.paidAmount = true;

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      toast.error('Please fill in all required fields');
      return;
    }

    // Trim invoice number
    const trimmedInvoiceNumber = newInvoice.invoice?.number?.trim() || '';
    if (!trimmedInvoiceNumber) {
      toast.error('Invoice number is required');
      return;
    }

    // Check for duplicate invoice numbers only for new invoices (not current invoices in limited reports)
    if (!(isMonitoringReport && mode === 'current' && currentInvoice)) {
      // Check both invoices prop and projectData.invoices for duplicates
      const allInvoices = [...(invoices || []), ...(projectData?.invoices || [])];

      const isDuplicate = allInvoices.some(
        (invoice: BaseInvoiceDTO) => invoice.invoice.number.trim().toLowerCase() === trimmedInvoiceNumber.toLowerCase(),
      );

      if (isDuplicate) {
        setValidationErrors({ ...errors, invoiceNumber: true });
        toast.error('Invoice number already exists');
        return;
      }
    }

    if (!newInvoice.invoice?.invoiceAmount || newInvoice.invoice.invoiceAmount <= 0) {
      toast.error('Invoice amount must be greater than 0');
      return;
    }

    // Add currentInvoice flag for limited reports based on mode
    const updatedInvoice = {
      ...newInvoice,
      invoice: {
        ...newInvoice.invoice,
        number: trimmedInvoiceNumber,
      },
      currentInvoice: isMonitoringReport ? mode === 'current' : undefined,
    };

    // Set loading state
    setIsExporting(true);

    // Export the invoice
    if (onExportInvoice) {
      onExportInvoice(updatedInvoice as CreateInvoiceDTO);
    }

    // Reset loading state after 2 seconds
    setTimeout(() => {
      setIsExporting(false);
    }, 2000);
  };

  const calculateNetOwed = (invoiceAmount: number, paidAmount: number) => {
    return invoiceAmount - paidAmount;
  };

  return (
    <Accordion expanded={true} sx={{ mb: 3, border: '2px solid', borderColor: 'primary.main' }}>
      {(isExportMode || (isMonitoringReport && mode === 'current') || !isMonitoringReport) && (
        <AccordionSummary>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <Typography variant='h6' color='primary'>
              {isExportMode ? 'Export Invoice' : isMonitoringReport && mode === 'current' ? 'Current Invoice' : 'New Invoice'}
            </Typography>
          </Box>
        </AccordionSummary>
      )}
      <AccordionDetails>
        <Grid container spacing={1}>
          {/* Recipient Information */}
          <Grid size={{ xs: 12 }}>
            <Typography variant='subtitle2' sx={{ mb: 1, fontWeight: 600 }}>
              Recipient Information
            </Typography>
          </Grid>

          <Grid size={{ xs: 4 }}>
            <CustomizedTextField
              label='Recipient Name *'
              placeholder='e.g., John Doe'
              value={newInvoice.recepient || ''}
              onChange={e => updateNewInvoiceField('recepient', e.target.value)}
              fullWidth
              size='small'
              error={validationErrors.recepient}
            />
          </Grid>
          <Grid container size={{ xs: 12 }} spacing={1}>
            <Grid size={{ xs: 2 }}>
              <CustomizedTextField
                label='Street'
                placeholder='Street'
                value={newInvoice.recepientAddress?.street || ''}
                onChange={e => updateNewInvoiceField('recepientAddress.street', e.target.value)}
                fullWidth
                size='small'
              />
            </Grid>
            <Grid size={{ xs: 2 }}>
              <Autocomplete
                freeSolo
                options={cityOptions.map(getCityString)}
                value={capitalize(newInvoice.recepientAddress?.city || '')}
                onChange={(event, newValue) => {
                  const isOptionFromList = !!newValue && cityOptions.map(getCityString).includes(newValue);
                  if (isOptionFromList) {
                    const cityName = newValue!.split(', ')[0];
                    const stateCode = newValue!.split(', ')[1];
                    const matchingState = stateOptions.find(option => option.stateCode === stateCode);
                    const stateName = capitalize(matchingState?.name || '') || '';
                    setRecipientStateManuallySet(false);
                    setRecipientCity(newValue || '');
                    setRecipientSelectedState(stateName);
                    setNewInvoice(prev => ({
                      ...prev,
                      recepientAddress: { ...prev.recepientAddress, city: cityName, state: stateName },
                    }));
                  } else {
                    setRecipientCity('');
                    setNewInvoice(prev => ({
                      ...prev,
                      recepientAddress: { ...prev.recepientAddress, city: newValue || '' },
                    }));
                  }
                }}
                onInputChange={(event, newInputValue, reason) => {
                  if (reason === 'input') {
                    setRecipientCity('');
                    setNewInvoice(prev => ({
                      ...prev,
                      recepientAddress: { ...prev.recepientAddress, city: newInputValue },
                    }));
                  }
                }}
                filterOptions={createFilterOptions({ limit: 10, matchFrom: 'start' })}
                onBlur={checkRecipientCityStateOnBlur}
                renderInput={params => <CustomizedTextField {...params} label='City' placeholder='City' size='small' />}
                size='small'
              />
            </Grid>
          </Grid>
          <Grid container size={{ xs: 12 }} spacing={1}>
            <Grid size={{ xs: 2 }} sx={{ minWidth: 0, display: 'flex', flexDirection: 'column' }}>
              <Autocomplete
                freeSolo
                options={stateOptions.map(getStateLabel)}
                value={recipientSelectedState}
                inputValue={recipientSelectedState}
                onInputChange={(event, newInputValue, reason) => {
                  if (reason === 'input') {
                    setRecipientStateManuallySet(true);
                    setRecipientSelectedState(newInputValue);
                  }
                }}
                onChange={(event, newValue) => {
                  const stateName = newValue || '';
                  setRecipientStateManuallySet(true);
                  setRecipientSelectedState(stateName);
                  updateNewInvoiceField('recepientAddress.state', stateName);
                }}
                onBlur={() => {
                  updateNewInvoiceField('recepientAddress.state', recipientSelectedState);
                  checkRecipientCityStateOnBlur();
                }}
                renderInput={params => (
                  <CustomizedTextField
                    {...params}
                    label='State'
                    placeholder='State'
                    size='small'
                    error={isRecipientCityStateInvalid}
                    helperText={
                      isRecipientCityStateInvalid
                        ? 'This city/state combination does not exist'
                        : recipientCityStateStatus === 'checking'
                          ? 'Verifying city/state…'
                          : ''
                    }
                  />
                )}
                size='small'
              />
            </Grid>
            <Grid size={{ xs: 2 }} sx={{ minWidth: 0, display: 'flex', flexDirection: 'column' }}>
              <CustomizedTextField
                label='Postal Code'
                placeholder='Postal Code'
                value={recipientPostalCode}
                onChange={e => {
                  const newValue = filterDigitsHyphens(e.target.value);
                  setRecipientPostalCode(newValue);
                  updateNewInvoiceField('recepientAddress.postalCode', newValue);
                }}
                size='small'
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>

          {/* Project Information */}
          <Grid size={{ xs: 12 }}>
            <Divider sx={{ my: 1 }} />
            <Typography variant='subtitle2' sx={{ mb: 1, fontWeight: 600 }}>
              Project Information
            </Typography>
          </Grid>

          <Grid size={{ xs: 4 }}>
            <CustomizedTextField
              label='Project Name *'
              value={newInvoice.projectName || ''}
              onChange={e => updateNewInvoiceField('projectName', e.target.value)}
              fullWidth
              size='small'
              error={validationErrors.projectName}
            />
          </Grid>
          <Grid container size={{ xs: 12 }} spacing={1}>
            <Grid size={{ xs: 2 }}>
              <CustomizedTextField
                label='Street'
                placeholder='Street'
                value={newInvoice.projectAddress?.street || ''}
                onChange={e => updateNewInvoiceField('projectAddress.street', e.target.value)}
                fullWidth
                size='small'
              />
            </Grid>
            <Grid size={{ xs: 2 }}>
              <Autocomplete
                freeSolo
                options={cityOptions.map(getCityString)}
                value={capitalize(newInvoice.projectAddress?.city || '')}
                onChange={(event, newValue) => {
                  const isOptionFromList = !!newValue && cityOptions.map(getCityString).includes(newValue);
                  if (isOptionFromList) {
                    const cityName = newValue!.split(', ')[0];
                    const stateCode = newValue!.split(', ')[1];
                    const matchingState = stateOptions.find(option => option.stateCode === stateCode);
                    const stateName = capitalize(matchingState?.name || '') || '';
                    setProjectStateManuallySet(false);
                    setProjectCityState(newValue || '');
                    setProjectSelectedState(stateName);
                    setNewInvoice(prev => ({
                      ...prev,
                      projectAddress: { ...prev.projectAddress, city: cityName, state: stateName },
                    }));
                  } else {
                    setProjectCityState('');
                    setNewInvoice(prev => ({
                      ...prev,
                      projectAddress: { ...prev.projectAddress, city: newValue || '' },
                    }));
                  }
                }}
                onInputChange={(event, newInputValue, reason) => {
                  if (reason === 'input') {
                    setProjectCityState('');
                    setNewInvoice(prev => ({
                      ...prev,
                      projectAddress: { ...prev.projectAddress, city: newInputValue },
                    }));
                  }
                }}
                filterOptions={createFilterOptions({ limit: 10, matchFrom: 'start' })}
                onBlur={checkProjectCityStateOnBlur}
                renderInput={params => <CustomizedTextField {...params} label='City' placeholder='City' size='small' />}
                size='small'
              />
            </Grid>
          </Grid>
          <Grid container size={{ xs: 12 }} spacing={1}>
            <Grid size={{ xs: 2 }} sx={{ minWidth: 0, display: 'flex', flexDirection: 'column' }}>
              <Autocomplete
                freeSolo
                options={stateOptions.map(getStateLabel)}
                value={projectSelectedState}
                inputValue={projectSelectedState}
                onInputChange={(event, newInputValue, reason) => {
                  if (reason === 'input') {
                    setProjectStateManuallySet(true);
                    setProjectSelectedState(newInputValue);
                  }
                }}
                onChange={(event, newValue) => {
                  const stateName = newValue || '';
                  setProjectStateManuallySet(true);
                  setProjectSelectedState(stateName);
                  updateNewInvoiceField('projectAddress.state', stateName);
                }}
                onBlur={() => {
                  updateNewInvoiceField('projectAddress.state', projectSelectedState);
                  checkProjectCityStateOnBlur();
                }}
                renderInput={params => (
                  <CustomizedTextField
                    {...params}
                    label='State'
                    placeholder='State'
                    size='small'
                    error={isProjectCityStateInvalid}
                    helperText={
                      isProjectCityStateInvalid
                        ? 'This city/state combination does not exist'
                        : projectCityStateStatus === 'checking'
                          ? 'Verifying city/state…'
                          : ''
                    }
                  />
                )}
                size='small'
              />
            </Grid>
            <Grid size={{ xs: 2 }} sx={{ minWidth: 0, display: 'flex', flexDirection: 'column' }}>
              <CustomizedTextField
                label='Postal Code'
                placeholder='Postal Code'
                value={projectPostalCode}
                onChange={e => {
                  const newValue = filterDigitsHyphens(e.target.value);
                  setProjectPostalCode(newValue);
                  updateNewInvoiceField('projectAddress.postalCode', newValue);
                }}
                size='small'
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
          <Grid size={{ xs: 4 }}>
            <CustomizedTextField
              label='Service'
              value={newInvoice.service || ''}
              onChange={e => updateNewInvoiceField('service', e.target.value)}
              fullWidth
              size='small'
            />
          </Grid>

          {/* Invoice Details */}
          <Grid size={{ xs: 12 }}>
            <Divider sx={{ my: 1 }} />
            <Typography variant='subtitle2' sx={{ mb: 1, fontWeight: 600 }}>
              Invoice Details
            </Typography>
          </Grid>
          <Grid container size={{ xs: 12 }} spacing={1} alignItems='center'>
            <Grid size={{ xs: 2 }}>
              <Typography variant='body2'>Invoice # *</Typography>
              <CustomizedTextField
                placeholder='e.g., INV-001'
                value={newInvoice.invoice?.number || ''}
                onChange={e => updateNewInvoiceField('invoice.number', e.target.value)}
                fullWidth
                size='small'
                error={validationErrors.invoiceNumber}
              />
            </Grid>
            <Grid size={{ xs: 2 }}>
              <Typography variant='body2' color={validationErrors.invoiceDate ? 'error' : 'inherit'}>
                Date *
              </Typography>
              <DynamicDatePicker
                fieldName='invoiceDate'
                value={newInvoice.invoice?.invoiceDate || null}
                onChange={(field, value) => updateNewInvoiceField('invoice.invoiceDate', value)}
              />
            </Grid>
            <Grid size={{ xs: 2 }}>
              <Typography variant='body2'>Amount Due *</Typography>
              <CustomizedTextField
                placeholder='0.00'
                value={newInvoice.invoice?.invoiceAmount || ''}
                onChange={e => updateNewInvoiceField('invoice.invoiceAmount', parseFloat(e.target.value) || 0)}
                InputProps={{
                  inputComponent: NumericFormatInput as any,
                }}
                fullWidth
                size='small'
                error={validationErrors.invoiceAmount}
              />
            </Grid>
            <Grid size={{ xs: 2 }}>
              <Typography variant='body2'>Amount Paid</Typography>
              <CustomizedTextField
                placeholder='0.00'
                value={newInvoice.invoice?.paidAmount || ''}
                onChange={e => updateNewInvoiceField('invoice.paidAmount', parseFloat(e.target.value) || 0)}
                InputProps={{
                  inputComponent: NumericFormatInput as any,
                }}
                fullWidth
                size='small'
                error={validationErrors.paidAmount}
              />
            </Grid>
            <Grid size={{ xs: 2 }}>
              <Typography variant='body2'>Net Owed:</Typography>
              <Typography
                variant='body2'
                sx={{ p: 1, backgroundColor: 'grey.100', borderRadius: 1, minHeight: 40, display: 'flex', alignItems: 'center' }}
              >
                {format(
                  calculateNetOwed(newInvoice.invoice?.invoiceAmount || 0, newInvoice.invoice?.paidAmount || 0).toString(),
                  'currency',
                )}
              </Typography>
            </Grid>
          </Grid>

          {/* Comment Section */}
          <Grid size={{ xs: 12 }}>
            {!showComment ? (
              <Box
                onClick={() => setShowComment(true)}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.75,
                  mt: 0.5,
                  cursor: 'pointer',
                  '&:hover .comment-plus': { borderColor: 'primary.main', color: 'primary.main' },
                  '&:hover .comment-label': { color: 'primary.main' },
                }}
              >
                <Typography
                  className='comment-label'
                  variant='caption'
                  sx={{ color: 'text.secondary', fontWeight: 500, transition: 'color 0.15s' }}
                >
                  Comments
                </Typography>
                <Box
                  className='comment-plus'
                  sx={{
                    width: 18,
                    height: 18,
                    borderRadius: '50%',
                    border: '1.5px dashed',
                    borderColor: 'text.disabled',
                    color: 'text.disabled',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'border-color 0.15s, color 0.15s',
                  }}
                >
                  <AddIcon sx={{ fontSize: 11 }} />
                </Box>
              </Box>
            ) : (
              <CustomizedTextField
                placeholder='Add a comment to appear on the invoice...'
                value={newInvoice.invoice?.comment || ''}
                onChange={e => updateNewInvoiceField('invoice.comment', e.target.value)}
                fullWidth
                size='small'
                multiline
                rows={3}
                sx={{ mt: 0.5 }}
              />
            )}
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Stack direction='row' spacing={1} sx={{ mt: 2, mb: 1 }}>
              {isExportMode ? (
                <Button variant='contained' onClick={handleExportInvoice} size='small' disabled={isExporting}>
                  {isExporting ? 'Exporting...' : 'Export Invoice'}
                </Button>
              ) : (
                <>
                  <Button
                    variant='contained'
                    onClick={handleSaveInvoice}
                    size='small'
                    disabled={isSaving || (isMonitoringReport && mode === 'current' && !hasChanges)}
                  >
                    {isSaving
                      ? 'Saving...'
                      : isMonitoringReport && mode === 'current'
                        ? !currentInvoice
                          ? 'Create Invoice'
                          : 'Save Invoice'
                        : 'Add Invoice'}
                  </Button>
                  {!isMonitoringReport && (
                    <Button variant='outlined' onClick={onCancel} size='small'>
                      Cancel
                    </Button>
                  )}
                </>
              )}
            </Stack>
          </Grid>
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};

export default CreateInvoice;
